<template>
  <n-modal
    v-model:show="showModal"
    style="width: 600px"
    class="custom-card"
    preset="card"
    :title="t('devices.pair.title')"
    :bordered="false"
    size="huge"
  >
    <div class="pair-devices__loading">
      <BfLoader />
      <div>
        {{ t('devices.pair.searching', { type: t(`devices.types.${type}`) }) }}
        <span class="pair-devices__amount">
          ({{
            t('devices.pair.devicesFound', {
              count: antDevices.length + bleDevices.length,
            })
          }})
        </span>
      </div>
    </div>

    <div class="pair-devices__list">
      <n-select
        v-model:value="selectedDevice"
        :disabled="antDevices.length == 0 && bleDevices.length == 0"
        :options="devicesOptions"
      ></n-select>

      <BfButton
        :state="selectedDevice ? ButtonState.Enabled : ButtonState.Disabled"
        @click="save"
      >
        {{ t('common.select') }}
      </BfButton>
    </div>
  </n-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { NModal, NSelect } from 'naive-ui'
import { SelectMixedOption } from 'naive-ui/es/select/src/interface'
import {
  antDevice,
  bleDevice,
  deviceType,
  deviceProtocol,
} from '@/types/device'
import { MqttTopic } from '@/mqttDefinitions/topics'

//Store
import { useMqttStore } from '@/store/mqttStore'
import { useDeviceStore } from '@/store/deviceStore'
import { storeToRefs } from 'pinia'
import { BfButton, BfLoader, ButtonState } from '@finetic/bikefitting-ui'

const props = withDefaults(
  defineProps<{
    type?: deviceType
  }>(),
  {
    type: deviceType.heartRate,
  },
)

const emit = defineEmits(['close'])
const mqttStore = useMqttStore()
const deviceStore = useDeviceStore()

const showModal = computed({
  get() {
    return true
  },
  set() {
    cancel()
    return false
  },
})

const { doSubscribe, doUnSubscribe, doRawPublish, client } = mqttStore
const { getAntDevicesByType, getBleDevicesByType } = storeToRefs(deviceStore)
const { t } = useI18n()

const antDevices = computed(() => getAntDevicesByType.value(props.type))
const bleDevices = computed(() => getBleDevicesByType.value(props.type))

const { updateAntList, updateBleList } = deviceStore

const antDevicesTopic = MqttTopic.DEVICES_ANT_LIST
const bleDevicesTopic = MqttTopic.DEVICES_BLE_LIST

const { getRawValueOnTopic } = storeToRefs(mqttStore)

doSubscribe(antDevicesTopic, client)
doSubscribe(bleDevicesTopic, client)

const antDevicesList = computed(() => getRawValueOnTopic.value(antDevicesTopic))
const bleDevicesList = computed(() => getRawValueOnTopic.value(bleDevicesTopic))

watch([antDevicesList, bleDevicesList], () => {
  updateAntList(antDevicesList.value as Array<antDevice>)
  updateBleList(bleDevicesList.value as Array<bleDevice>)

  const ant = antDevices.value
  const ble = bleDevices.value

  if (ant.length > 0) {
    selectedDevice.value = `ANT|${ant[0].id}`
  } else if (ble.length > 0) {
    selectedDevice.value = `BLE|${ble[0].id}`
  } else {
    selectedDevice.value = 0
  }
})

const selectedDevice = ref()

const devicesOptions = computed(() => {
  if (antDevices.value.length > 0 || bleDevices.value.length > 0) {
    const options = new Array<SelectMixedOption>()

    options.push({
      label: t('devices.pair.selectDevice'),
      value: 0,
      disabled: true,
    })

    antDevices.value.forEach((device: any) => {
      const option = {
        label: `${device.name} (${device.id})`,
        value: `ANT|${device.id}`,
      }

      options.push(option)
    })

    bleDevices.value.forEach((device: any) => {
      const option = {
        label: `${device.name} (${device.id})`,
        value: `BLE|${device.id}`,
      }

      options.push(option)
    })

    return options
  } else {
    return [
      {
        label: t('devices.pair.noDevicesFound'),
        value: 0,
        disabled: true,
      },
    ]
  }
})

const deviceConnectTopic = (protocol: deviceProtocol) => {
  switch (protocol.toUpperCase()) {
    case deviceProtocol.ANT:
      return MqttTopic.DEVICES_ANT_CONNECT
    case deviceProtocol.BLE:
      return MqttTopic.DEVICES_BLE_CONNECT
    default:
      return undefined
  }
}

const save = () => {
  const [protocol, id] = selectedDevice.value.split('|')

  let newDevice: antDevice | bleDevice | undefined = undefined
  const topic = deviceConnectTopic(protocol)

  switch (protocol.toUpperCase()) {
    case deviceProtocol.ANT:
      newDevice = antDevices.value.find((x) => x.id == id)
      break

    case deviceProtocol.BLE:
      newDevice = bleDevices.value.find((x) => x.id == id)
      break
  }

  if (newDevice && topic) {
    const payload = JSON.stringify({
      name: newDevice['name'],
      id: newDevice['id'],
      type: newDevice['type'],
    })

    doRawPublish(topic, payload, client).then(() => {
      emit('close')
    })
  }
}

const cancel = () => {
  doUnSubscribe(antDevicesTopic, client)
  doUnSubscribe(bleDevicesTopic, client)

  emit('close')
}
</script>

<style lang="scss">
.pair-devices {
  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    padding: 48px 0;
  }

  &__amount {
    opacity: 0.6;
  }

  &__list {
    display: flex;
    gap: 1.2rem;
  }
}
</style>
